import { useReactiveVar } from "@apollo/client";
import {
  Formik,
  Form as FormikForm,
  FormikProps,
  ErrorMessage,
  Field,
} from "formik";
import React, { useEffect, useRef, useState } from "react";
import {
  Form as BootstrapForm,
  Button,
  Col,
  Row,
  Spinner,
} from "react-bootstrap";
import { ExclamationCircle, InfoCircle } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import PopoverBtn from "../../../components/inputs/popoverBtn";
import ScrollToFieldError from "../../../components/ScrollToFieldError";
import CustomToast from "../../../components/tooltips/CustomToast";
import { FAILED_DOMAIN_STATUSES } from "../../../constants";
import { useGraphqlErrors } from "../../../hooks/useGraphqlErrors";
import {
  whiteLabelJRActive,
  whiteLabelJRStep,
  whiteLabelStep,
} from "../../../qraphql/globalVar/state";
import {
  useCreateOrUpdateWhiteLabelDomainMutation,
  useOrganizerWhiteLabelQuery,
  useRetryWhiteLabelDomainSetupMutation,
} from "../../../qraphql/whiteLabel/whiteLabel.hooks";
import { DomainType, WhiteLaelStepEnum } from "../../../types";
import { validationTicketSudomain } from "../../../validations/whiteLabel";

interface ISubdomainForm {
  subdomain: string;
}

interface ISubDomainTicketProps {
  refetchWhiteLabelInfo: () => void;
}

export default function SubDomainTicket({
  refetchWhiteLabelInfo,
}: ISubDomainTicketProps) {
  const { t } = useTranslation();
  const formRef = useRef<FormikProps<ISubdomainForm>>(null);
  const [isShowAlert, setIsShowAlert] = useState(false);
  const { getErrorMessages } = useGraphqlErrors();

  const { data: whiteLabelInfo } = useOrganizerWhiteLabelQuery();
  const whiteLabelJRActiveValue = useReactiveVar(whiteLabelJRActive);

  const [
    createOrUpdateWhiteLabelDomain,
    {
      loading: createOrUpdateWhiteLabelDomainLoading,
      error: createOrUpdateWhiteLabelDomainError,
    },
  ] = useCreateOrUpdateWhiteLabelDomainMutation();
  const [
    retryWhiteLabelDomainSetup,
    { loading: retryWhiteLabelDomainSetupLoading },
  ] = useRetryWhiteLabelDomainSetupMutation();

  useEffect(() => {
    if (isShowAlert) {
      setTimeout(() => {
        setIsShowAlert(false);
        refetchWhiteLabelInfo();
      }, 3000);
    }
  }, [isShowAlert]);

  const onSubmit = (values: ISubdomainForm) => {
    createOrUpdateWhiteLabelDomain({
      variables: {
        domainType: DomainType.TicketSubdomain,
        whiteLabelId: whiteLabelInfo.organizer?.whiteLabel?.id,
        domain: values.subdomain,
      },
      onCompleted: () => {
        setIsShowAlert(true);
        whiteLabelStep(WhiteLaelStepEnum.MediaDesign);
        if (whiteLabelJRActiveValue) {
          whiteLabelJRStep(6);
        }
      },
    });
  };

  const initialValues: ISubdomainForm = {
    subdomain:
      whiteLabelInfo.organizer?.whiteLabel?.domainType ===
      DomainType.TicketSubdomain
        ? whiteLabelInfo.organizer?.whiteLabel?.domain?.replace(
            ".me-ticket.com",
            ""
          )
        : "",
  };

  const handleTrySetup = () => {
    if (retryWhiteLabelDomainSetupLoading) return;
    retryWhiteLabelDomainSetup({
      variables: { whiteLabelId: whiteLabelInfo.organizer?.whiteLabel?.id },
      onCompleted: () => {
        refetchWhiteLabelInfo();
      },
    });
  };

  return (
    <>
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationTicketSudomain(t)}
        enableReinitialize
      >
        {({ isValid, values, submitCount }) => {
          return (
            <FormikForm>
              <ScrollToFieldError />
              <Row className="g-3 ms-3 pt-1">
                <Col md={8}>
                  <BootstrapForm.Group
                    controlId="formSubdomain"
                    className="w-100 position-relative overflow-hidden"
                  >
                    <span
                      className="input-content position-absolute top-50 translate-middle-y pe-none text-nowrap"
                      style={{ zIndex: 1, left: 10 }}
                    >
                      <span className="opacity-0">{values.subdomain}</span>
                      <span className="text-info ps-1">.me-ticket.com</span>
                    </span>
                    <Field
                      as={BootstrapForm.Control}
                      type="text"
                      name="subdomain"
                      className="py-3 subdomain-input bg-transparent position-relative"
                      style={{ zIndex: 2 }}
                      autoComplete="off"
                    />

                    <div
                      className="position-absolute top-0 end-0 mt-3 w-auto me-3"
                      style={{ zIndex: 2 }}
                    >
                      <PopoverBtn
                        text={t("whiteLabel.It’s your default subdomain name")}
                        icon={<InfoCircle />}
                      />
                    </div>
                  </BootstrapForm.Group>
                  <ErrorMessage
                    name="subdomain"
                    component="div"
                    className="invalid-feedback text-start error-field d-block"
                  />
                  {createOrUpdateWhiteLabelDomainError && (
                    <div className="invalid-feedback text-start d-block">
                      {getErrorMessages(createOrUpdateWhiteLabelDomainError)[0]}
                    </div>
                  )}
                </Col>
                <Col md={4}>
                  <Button
                    variant="success"
                    className="fw-bold p-3 w-100"
                    type="submit"
                    disabled={
                      (submitCount > 0 && !isValid) ||
                      createOrUpdateWhiteLabelDomainLoading
                    }
                  >
                    {whiteLabelInfo.organizer?.whiteLabel?.domain &&
                    whiteLabelInfo.organizer?.whiteLabel.domainType ===
                      DomainType.TicketSubdomain
                      ? t("whiteLabel.Change Subdomain")
                      : t("whiteLabel.Save Subdomain")}
                    {createOrUpdateWhiteLabelDomainLoading && (
                      <Spinner
                        animation="border"
                        role="status"
                        size="sm"
                        className="ms-2"
                      />
                    )}
                  </Button>
                </Col>
              </Row>
              {FAILED_DOMAIN_STATUSES.includes(
                whiteLabelInfo.organizer?.whiteLabel?.domainStatus
              ) && (
                <div className="text-start text-danger mt-3 bg-danger-light p-3 rounded d-flex align-items-center gap-3">
                  <div>
                    <ExclamationCircle />
                  </div>
                  <div className="fs-7">
                    <div>
                      {t(
                        "whiteLabel.We noticed that domain wasn't updated correctly. Please"
                      )}{" "}
                      <span
                        className="text-danger text-decoration-underline cursor-pointer"
                        onClick={handleTrySetup}
                      >
                        {t("whiteLabel.try setup again")}
                      </span>
                      {retryWhiteLabelDomainSetupLoading && (
                        <Spinner
                          animation="border"
                          size="sm"
                          className="ms-2"
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}

              <CustomToast
                isShow={isShowAlert}
                handleClose={() => setIsShowAlert(false)}
                message={t("whiteLabel.Subdomain successfully changed")}
              />
            </FormikForm>
          );
        }}
      </Formik>
    </>
  );
}
